import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { BaseDto } from "../../util/baseDto";
import { useNotification } from "../../../components/common/GlobalNotification";
import { NOTIFICATION_CHANNELS_QUERY_KEY } from "../channels";
import { notifications_api_requests } from "..";

const PATH = "/notifications";

// ============================================================================
// TypeScript Interfaces
// ============================================================================

/**
 * Notification Provider
 * Represents a service provider for a notification channel (MSG91, AWS_SNS, TWILIO, etc.)
 * Note: The 'id' field represents the junction table ID (provider-channel association ID), not the provider ID.
 */
export interface NotificationProvider extends BaseDto {
  providerCode: string;
  providerName: string;
  channelCode: string;
  channelName: string;
  isEnabled: boolean;
  priority: number;
  description: string | null;
}

/**
 * Request body for updating provider status
 */
export interface NotificationProviderStatus {
  channelCode: string; // Required field for provider-channel association
  isEnabled: boolean;
}

// ============================================================================
// Query Keys
// ============================================================================

export const NOTIFICATION_PROVIDERS_QUERY_KEY = "notifications-providers";

// ============================================================================
// Provider Management Hooks
// ============================================================================

/**
 * Fetch all notification providers
 * GET /api/v1/notifications/providers
 */
export const useGetProviders = () => {
  return useQuery({
    queryKey: [NOTIFICATION_PROVIDERS_QUERY_KEY, "all"],
    queryFn: async (): Promise<NotificationProvider[]> =>
      notifications_api_requests.get(`${PATH}/providers`),
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
};

/**
 * Fetch a specific notification provider by provider code
 * GET /api/v1/notifications/providers/{providerCode}
 * @param providerCode - The provider code (MSG91, AWS_SNS, TWILIO, etc.)
 */
export const useGetProvider = (providerCode?: string) => {
  return useQuery({
    queryKey: [NOTIFICATION_PROVIDERS_QUERY_KEY, "by-code", providerCode],
    queryFn: async (): Promise<NotificationProvider> =>
      notifications_api_requests.get(`${PATH}/providers/${providerCode}`),
    enabled: !!providerCode,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
};

/**
 * Fetch all providers for a specific channel
 * GET /api/v1/notifications/channels/{channelCode}/providers
 * @param channelCode - The channel code (SMS, EMAIL, PUSH, WHATSAPP)
 */
export const useGetProvidersByChannel = (channelCode?: string) => {
  return useQuery({
    queryKey: [NOTIFICATION_PROVIDERS_QUERY_KEY, "by-channel", channelCode],
    queryFn: async (): Promise<NotificationProvider[]> =>
      notifications_api_requests.get(
        `${PATH}/channels/${channelCode}/providers`,
      ),
    enabled: !!channelCode,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
};

/**
 * Update a notification provider status
 * PUT /api/v1/notifications/providers/{providerCode}/status
 * Request body now requires channelCode to identify the specific provider-channel association
 */
export const useUpdateProviderStatus = () => {
  const queryClient = useQueryClient();
  const { setSuccessNotification, setErrorNotification } = useNotification();

  return useMutation({
    mutationFn: async ({
      providerCode,
      status,
    }: {
      providerCode: string;
      status: NotificationProviderStatus;
    }): Promise<NotificationProvider> =>
      notifications_api_requests.put(
        `${PATH}/providers/${providerCode}/status`,
        status,
      ),

    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: [NOTIFICATION_PROVIDERS_QUERY_KEY],
      });
      queryClient.invalidateQueries({
        queryKey: [NOTIFICATION_CHANNELS_QUERY_KEY],
      });
      setSuccessNotification(
        `Provider ${data.providerName} status updated successfully`,
      );
    },

    onError: (error: any) => {
      const message =
        error?.response?.data?.message || "Failed to update provider status";
      setErrorNotification(message);
    },
  });
};

// Made with Bob
